// Foreman Commission Statement — per-chit, per-month commission for one FY.
// Renders as a 12-column grid (Apr..Mar) × N chits + a totals column. KPI strip
// at the top, per-chit summary table at the bottom.

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { fiscal_year_label, format_inr, short_month_label } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { DateFilterBar } from '../components/DateFilterBar.js';

interface ChitRow {
  chit_id: string;
  name_ta: string;
  name_en: string;
  monthly_collected_paise: number[];
  monthly_commission_paise: number[];
  total_collected_paise: number;
  total_commission_paise: number;
}
interface ForemanData {
  fy_label: string;
  fy_start: string;
  fy_end: string;
  commission_pct: number;
  headline: { total_collected_paise: number; total_commission_paise: number; effective_pct: number };
  months: string[];
  chits: ChitRow[];
}

export function ReportsForemanCommissionPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [fy_label, set_fy_label] = useState(fiscal_year_label());

  const q = useQuery({
    queryKey: ['report-foreman-commission', fy_label],
    queryFn: () => orpc.reports.foreman_commission({ fy_label }) as Promise<ForemanData>,
  });
  const data = q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = ['month,chit_id,chit_name,collected_rupees,commission_pct,commission_rupees'];
    for (const c of data.chits) {
      data.months.forEach((m, i) => {
        if (c.monthly_collected_paise[i] === 0) return;
        lines.push(
          [
            m,
            c.chit_id,
            (c.name_en || c.name_ta).replace(/,/g, ' '),
            ((c.monthly_collected_paise[i] ?? 0) / 100).toFixed(2),
            data.commission_pct,
            ((c.monthly_commission_paise[i] ?? 0) / 100).toFixed(2),
          ].join(','),
        );
      });
    }
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `foreman-commission-${fy_label}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.foreman_commission_pdf({ fy_label }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('ஃபோர்மேன் கமிஷன் கூற்று', 'Foreman Commission')}</div>
          <div className="h-sub">FY {fy_label}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateFilterBar mode="fy" fy_label={fy_label} on_change={set_fy_label} />
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi"><div className="kpi-label">{t('மொத்த வசூல்', 'Total collected')}</div><div className="kpi-value">{data ? format_inr(data.headline.total_collected_paise) : '—'}</div></div>
        <div className="kpi primary"><div className="kpi-label">{t('கமிஷன்', 'Commission earned')}</div><div className="kpi-value">{data ? format_inr(data.headline.total_commission_paise) : '—'}</div></div>
        <div className="kpi"><div className="kpi-label">{t('நிஜ விகிதம்', 'Effective rate')}</div><div className="kpi-value">{data ? `${data.headline.effective_pct.toFixed(2)}%` : '—'}</div><div className="kpi-sub">{data ? `${t('அமைப்பு', 'Setting')}: ${data.commission_pct}%` : ''}</div></div>
      </div>

      {q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(q.error)}</div>}
      {data && data.chits.length === 0 && (
        <div className="card" style={{ padding: 30, textAlign: 'center', color: 'var(--ink-3)' }}>
          {t('இந்த FY-ல் வசூல் இல்லை', 'No collections in this FY')}
        </div>
      )}

      {data && data.chits.length > 0 && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">{t('மாதம் × சீட்டு கிரிட்', 'Month × chit grid')}</div>
            <div className="card-sub mono">{data.chits.length} {t('சீட்டுகள்', 'chits')}</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600, position: 'sticky', left: 0, background: 'var(--bg-elev)' }}>{t('சீட்டு', 'Chit')}</th>
                  {data.months.map((m) => (
                    <th key={m} style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, minWidth: 70 }}>{short_month_label(m, lang)}</th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600, background: 'var(--bg-elev)' }}>{t('மொத்தம்', 'Total')}</th>
                </tr>
              </thead>
              <tbody>
                {data.chits.map((c) => (
                  <tr key={c.chit_id} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '6px 10px', position: 'sticky', left: 0, background: 'white' }} className={lang === 'ta' ? 'tamil' : ''}>
                      <Link to={`/chits/${c.chit_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                        {(lang === 'ta' ? c.name_ta : c.name_en) || '—'}
                      </Link>
                    </td>
                    {c.monthly_commission_paise.map((p, i) => (
                      <td key={i} style={{ textAlign: 'right', padding: '6px 8px', color: p === 0 ? 'var(--ink-3)' : 'var(--ink)' }} className="mono">
                        {p === 0 ? '—' : format_inr(p)}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }} className="mono">{format_inr(c.total_commission_paise)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--line)', background: 'var(--bg-elev)' }}>
                  <td style={{ padding: '8px 10px', fontWeight: 600 }}>{t('மொத்தம்', 'Total')}</td>
                  {data.months.map((m, i) => {
                    const month_total = data.chits.reduce((s, c) => s + (c.monthly_commission_paise[i] ?? 0), 0);
                    return <td key={m} style={{ textAlign: 'right', padding: '8px 8px' }} className="mono">{month_total > 0 ? format_inr(month_total) : '—'}</td>;
                  })}
                  <td style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 600 }} className="mono">{format_inr(data.headline.total_commission_paise)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 1200, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="fc-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
